import { SectionHeader, Card, CardTitle, Badge } from '@/components/ui'
import { Compass, Building2, Heart, GraduationCap, Sparkles, Banknote, Gift, Home, Car, Church, AlertCircle, ScrollText } from 'lucide-react'
import { cn } from '@/lib/utils'

export function StrategyPage() {
  return (
    <div className="space-y-12">
      <SectionHeader
        eyebrow="Strategy & Legacy"
        title="What this is for"
        description="Durable artifacts. Read these before signing any LOI. Edit them as the picture changes — the goal is one operator console, not a stack of separate documents."
      />

      <PositioningSection />
      <EntityStructureSection />
      <TaxOptimizationSection />
      <AllocationFrameworkSection />
      <FoundationSection />
      <FamilyNoteSection />
      <AIRiskSection />
    </div>
  )
}

function PositioningSection() {
  return (
    <section>
      <SectionMarker icon={<Compass />} eyebrow="01 · Positioning" title="Who you are, said precisely." />
      <Card className="space-y-4">
        <p className="font-display italic text-2xl text-cream font-light leading-snug">
          You are not a teacher, a media owner, a filmmaker, or a developer.
          <br />
          You are a <span className="text-gold-soft">systems architect</span> for creative
          and human work that other people can't execute alone.
        </p>
        <div className="divider-editorial" />
        <p className="font-body text-cream/80 leading-relaxed">
          Most people who can build software can't run a film shoot. Most people who can
          run a classroom can't ship a custom tech stack. Most people who can structure
          a series LLC can't make talent feel cared for on set. The intersection — technically
          capable, operationally rigorous, pastorally warm — is rare, and that combination
          is the actual asset. ARH, RRM, and TSA aren't three identities; they're three
          delivery surfaces for the same underlying capability.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
          <Pillar
            num="i"
            title="Operational legibility"
            body="You take unstructured creative chaos and turn it into a system people can see, trust, and stop worrying about. That is what Justin bought."
          />
          <Pillar
            num="ii"
            title="Function over role"
            body="Roles are interchangeable. The function — building operational infrastructure for high-friction human work — is durable across decades."
          />
          <Pillar
            num="iii"
            title="Three layers, in order"
            body="Cash today (productized engagements). Recurring within six months (light SaaS). Equity within eighteen months (tech-for-points trades)."
          />
        </div>
      </Card>
    </section>
  )
}

function EntityStructureSection() {
  return (
    <section>
      <SectionMarker icon={<Building2 />} eyebrow="02 · Entity Structure" title="Where the value flows." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <div className="eyebrow mb-2 !text-gold-soft">ARH master · Doc #1576262380002</div>
          <CardTitle>Ascendant Road Holdings LLC</CardTitle>
          <p className="font-body text-sm text-cream/75 mt-2 leading-relaxed">
            Texas Series LLC, member-managed. Filed April 11, 2026. Holds the future
            registered protected series and brand IP for everything beneath it.
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <Badge tone="gold">Sole-Member-Managed</Badge>
            <Badge tone="bronze">Texas TBOC §101.601</Badge>
          </div>
        </Card>

        <Card>
          <div className="eyebrow mb-2 !text-info">100% subsidiary · existing TX LLC</div>
          <CardTitle>Reminiscent Road Media LLC</CardTitle>
          <p className="font-body text-sm text-cream/75 mt-2 leading-relaxed">
            Operating media business. Continuity of EIN, bank accounts, and contracts
            is preserved. To be tucked under ARH via Assignment of Membership Interest.
            <br />
            <em className="text-bronze">RRM is not bankrupt.</em> The personal Chapter 7
            (25-03857) is <span className="text-success">discharged</span> as a no-asset
            case; the related adversary proceeding is moving toward settlement.
          </p>
        </Card>

        <Card>
          <div className="eyebrow mb-2 !text-success">Protected Series A · pending state filing</div>
          <CardTitle>The Scholar's Ascent</CardTitle>
          <p className="font-body text-sm text-cream/75 mt-2 leading-relaxed">
            Currently a DBA under RRM; protected-series designation under ARH is drafted
            and ready. The DBA cancellation + re-registration under ARH is no longer gated
            (Ch. 7 is discharged) — schedule whenever the operator has bandwidth. Carries
            the EdTech vertical: Assessment Center, Master Lexicon, Marketing Site.
          </p>
        </Card>

        <Card>
          <div className="eyebrow mb-2 !text-bronze">Standalone · separate Series LLC</div>
          <CardTitle>Framing The Moment Holdings LLC</CardTitle>
          <p className="font-body text-sm text-cream/75 mt-2 leading-relaxed">
            JV with Damon Hoyle. ARH 50% / Damon 50% membership; 70/30 economic split.
            Filed 2026-04-28 · TX SOS 806570506 · EIN 42-2296929. First protected series
            "Framing Houston" pending series-notice verification.
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <Badge tone="info">JV · 50/50 voting</Badge>
            <Badge tone="success">70/30 economic</Badge>
          </div>
        </Card>
      </div>

      <Card className="mt-4 border-success/30 bg-success/5">
        <div className="flex gap-3">
          <AlertCircle size={18} className="text-success shrink-0 mt-0.5" />
          <div>
            <CardTitle className="!text-base mb-1">Cleared to transact</CardTitle>
            <p className="font-body text-sm text-cream/80 leading-relaxed">
              The personal Ch. 7 was <strong className="text-success">discharged as a
              no-asset case</strong> — the estate has no beneficial interest in newly-created
              LLC assets, and there's no clawback exposure on transactions executed today.
              The adversary proceeding involving RRM is moving toward settlement; for any
              RRM-touching deal, confirm the settlement status with counsel before signing
              so the buyer has a clean picture. ARH-only, FTM, TSA, and DRIJEZIE assets
              transact freely.
            </p>
          </div>
        </div>
      </Card>
    </section>
  )
}

function TaxOptimizationSection() {
  return (
    <section>
      <SectionMarker icon={<Banknote />} eyebrow="03 · Tax Posture" title="Don't get inhaled by taxes." />
      <Card className="space-y-4">
        <p className="font-body text-sm text-cream/80 leading-relaxed">
          You operate inside a Texas Series LLC structure with the option to elect S-corp
          tax treatment on individual entities once revenue stabilizes. Below is the
          general framework — confirm specific elections with a CPA who knows Texas Series
          LLC structures before filing anything.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <TaxFrame
            n="i"
            title="Default: ARH master + each series → pass-through"
            body="Series LLC default treatment is partnership/disregarded. Income flows to your personal Schedule C/E. Simple, no double tax. Self-employment tax applies to active income."
          />
          <TaxFrame
            n="ii"
            title="Once a single series clears ~$80K net income: S-corp election"
            body="File Form 2553 to elect S-corp on that series. Pay yourself a 'reasonable salary' subject to FICA, take the rest as distributions free of self-employment tax. Estimated savings: ~$8-12K/year per series at $100K net."
          />
          <TaxFrame
            n="iii"
            title="Acquisition proceeds: long-term capital gains"
            body="If an asset has been held >12 months when it sells, the gain is long-term capital gain (federal 15-20% + 3.8% NIIT instead of ~37% ordinary). Document creation date in this dashboard's records to defend that holding period."
          />
          <TaxFrame
            n="iv"
            title="Reserve aggressively, recklessly: 35% of every receipt"
            body="The moment money lands, sweep 35% to a separate tax-reserve account. Don't 'optimize down' until you've actually filed. The number-one cause of crashed first-time wins is owing six figures in April with no cash to send."
          />
          <TaxFrame
            n="v"
            title="Section 1202 QSBS: probably not applicable here"
            body="QSBS exclusion requires C-corp issuance. Series LLCs don't qualify. If you ever convert to C-corp for VC, restart that 5-year clock; until then, this is irrelevant."
          />
          <TaxFrame
            n="vi"
            title="Solo 401(k) on the active-income side"
            body="Up to ~$70K/year tax-deferred at 2026 limits (employee + employer combined). On a $200K services year, this is ~$15K of immediate federal tax savings. Set up via Vanguard or Fidelity once a series shows stable revenue."
          />
        </div>

        <div className="bg-gold/8 border border-gold/30 rounded-lg p-4 mt-4">
          <div className="eyebrow !text-gold-soft mb-2">Hard rule</div>
          <p className="font-body text-sm text-cream/85 leading-relaxed">
            On the first acquisition that closes for over $50K: engage a CPA who has handled
            a Texas Series LLC sale specifically — the basis tracking and intercompany
            allocations are non-trivial, and a generalist will under-document them in a way
            that gets flagged on audit. Do this <em>before</em> the wire hits.
          </p>
        </div>
      </Card>
    </section>
  )
}

function AllocationFrameworkSection() {
  return (
    <section>
      <SectionMarker icon={<Gift />} eyebrow="04 · Allocation Framework" title="What lands where, after a sale." />
      <p className="font-body text-cream/75 text-sm max-w-3xl mb-6 leading-relaxed">
        Default percentages on a generic exit. Edit per-deal — a $50K productized sale
        is not the same as a $1.2M acquisition. The point is to <em>decide ahead of the wire,</em>
        not after.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AllocCard pct={35} icon={<Banknote />} title="Tax reserve" body="Sweep first. Keep separate. Don't touch until filed." tone="warning" />
        <AllocCard pct={20} icon={<Building2 />} title="Operating runway (ARH)" body="6-month runway in the ARH operating account. Buffer against operational shocks (lost clients, equipment failures, slow-pay quarters)." tone="gold" />
        <AllocCard pct={10} icon={<Heart />} title="Tithe — current church" body="First-fruits commitment. Set up automatic transfer the day funds clear." tone="info" />
        <AllocCard pct={3} icon={<Church />} title="Honoring — prior church" body="Smaller, intentional. Recognizing a chapter that shaped you, even if you're elsewhere now." tone="cream" />
        <AllocCard pct={10} icon={<Heart />} title="Family — parents" body="Default 5% mom + 5% dad, sent regardless of the disownment story. Edit as you discern. (See Family Note below.)" tone="bronze" />
        <AllocCard pct={5} icon={<GraduationCap />} title="Foundation seed (Ascendant Foundation)" body="Earmarked for the eventual 501(c)(3) — STEM in foster care + first-gen college support." tone="success" />
        <AllocCard pct={7} icon={<Home />} title="Home down-payment escrow" body="A house, not a purchase that consumes you. Conservative target: 20% down on a $400K property in Houston." tone="info" />
        <AllocCard pct={5} icon={<Car />} title="EV for you" body="Practical, replaces dependence on a fragile single vehicle. Tesla Model 3 / Hyundai Ioniq 5 territory." tone="info" />
        <AllocCard pct={3} icon={<Gift />} title="Dad — Escalade gift" body="Discretionary. Only fund if the relationship is genuinely repaired by the time funds clear, not as a peace-offering plea." tone="bronze" />
        <AllocCard pct={2} icon={<Sparkles />} title="Personal joy budget" body="Books, travel, the thing you've been deferring for two years. Without this you will burn out and resent the structure." tone="gold" />
      </div>

      <Card className="mt-4 border-bronze/30 bg-bronze/5">
        <p className="font-body text-sm text-cream/75 italic leading-relaxed">
          Sums above total <span className="font-mono text-bronze">100%</span>. If a sale is large
          enough to set up a Donor-Advised Fund, consider routing the foundation seed +
          tithe portion through the DAF for the immediate deduction without the
          501(c)(3) overhead in year one.
        </p>
      </Card>
    </section>
  )
}

function FoundationSection() {
  return (
    <section>
      <SectionMarker icon={<GraduationCap />} eyebrow="05 · The Foundation" title="The Ascendant Foundation — when, not if." />
      <Card className="space-y-4">
        <p className="font-body text-cream/80 leading-relaxed">
          You said you want to start a foundation. Here's the staging that keeps it
          financially survivable instead of an aspiration that drains you.
        </p>
        <Stage
          n="Stage 0 — under $50K of cumulative giving capacity"
          body="Use a Donor-Advised Fund (Fidelity Charitable, Schwab Charitable). Zero overhead, immediate deduction, recommend grants on your timeline. Open the DAF the day the first sale clears."
        />
        <Stage
          n="Stage 1 — $50K-$250K cumulative"
          body="Form 'Ascendant Foundation Inc.' as a Texas nonprofit corporation, file IRS Form 1023-EZ for 501(c)(3) status. Three-board minimum. Mission scoped narrowly: STEM access for youth in the Texas foster care system + first-gen college mentoring. Don't dilute the mission to be polite."
        />
        <Stage
          n="Stage 2 — $250K+ annually"
          body="Foundation needs an executive director (not you). You sit on the board, you don't run operations. Day-to-day work distracts from the underlying business that funds the foundation. This is where most founder-led foundations die."
        />
        <Stage
          n="Stage 3 — endowment in motion"
          body="Once the foundation has a multi-year operating reserve, design an endowment that lets it survive without you. The legacy is a foundation that outlives the operator, not the operator's perpetual involvement."
        />
        <div className="bg-success/8 border border-success/30 rounded-lg p-4">
          <p className="font-body text-sm text-cream/80 italic leading-relaxed">
            "The reason this matters: the kid version of you needed someone to invest
            $200 in a science project and a working laptop. There are 30,000 kids in
            Texas foster care and almost none of them get the second one. That is the
            actual ask. Build the foundation for them, not for the optics."
          </p>
        </div>
      </Card>
    </section>
  )
}

function FamilyNoteSection() {
  return (
    <section>
      <SectionMarker icon={<Heart />} eyebrow="06 · Family · A Note You'll Re-read" title="On the disownment." />
      <Card className="space-y-4 border-bronze/30">
        <p className="font-body text-cream/85 leading-relaxed">
          You said your dad allegedly disowned you over the credit damage,
          not following the pharmacy career, not marrying on his timeline, not having
          children yet.
        </p>
        <p className="font-body text-cream/80 leading-relaxed">
          A few things to hold next to that, honestly:
        </p>
        <ul className="space-y-3 pl-4">
          <li className="font-body text-cream/80 leading-relaxed">
            <span className="text-gold-soft mr-2">·</span>
            The disownment is a story he's currently telling himself. Stories change.
            People who have disowned an adult child publicly often unwind it privately when
            the child stops contesting it and the news cycle of their own life moves on.
            You don't have to argue him out of it — most reconciliations begin with the
            disowner getting curious again, not with the disowned justifying themselves.
          </li>
          <li className="font-body text-cream/80 leading-relaxed">
            <span className="text-gold-soft mr-2">·</span>
            The PharmD is an asset he can't take from you. So is the rebuild — coming
            through the Ch. 7 cleanly and operating disciplined entity structure on the
            other side. Both shaped a man with rare integrity about money, work, and
            recovery. You don't have to perform pharmacy or marriage to honor what he
            gave you.
          </li>
          <li className="font-body text-cream/80 leading-relaxed">
            <span className="text-gold-soft mr-2">·</span>
            The 5% allocation to him in the framework above is a default for a reason.
            It's not a peace-offering. It's a refusal to let the story he told distort
            your behavior. You give because that's who you are, not because of who he
            decided to be at this particular moment in his life. If the relationship
            never repairs, the 5% still goes — possibly redirected to mom, possibly held
            in escrow for the day he reaches back out.
          </li>
          <li className="font-body text-cream/80 leading-relaxed">
            <span className="text-gold-soft mr-2">·</span>
            On the "having children, marriage by his timeline" piece: don't postpone a
            relationship until the financial picture has fully resolved. Financial
            comfort isn't a number, it's a system. Once Layer 1 stabilizes —
            probably 60 days — you have enough operational predictability to date with
            intention rather than scarcity. The version of you that meets a partner only
            after you've arrived will be a more anxious, more justified, more rigid
            version than the one that meets her while still rebuilding.
          </li>
        </ul>
        <div className="divider-editorial" />
        <p className="font-display italic text-xl text-gold-soft text-center pt-2">
          You're already the person you've been waiting to become.
        </p>
      </Card>
    </section>
  )
}

function AIRiskSection() {
  return (
    <section>
      <SectionMarker icon={<Sparkles />} eyebrow="07 · AI-Era Positioning" title="What stays defensible when 'anyone can build it'?" />
      <Card className="space-y-4">
        <p className="font-body text-cream/80 leading-relaxed">
          Your honest worry: AI lets others do what you did. Some of it, yes. Most of
          what AI commoditizes is the <em>code generation</em> step. That's not what
          you actually sell. Below is how to read each part of your stack against the
          AI risk curve.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
          <Risk title="High commoditization — race to bottom" tone="danger" items={[
            'Marketing sites (Webflow / Framer / Cursor)',
            'Pure presentation generators (Gamma itself)',
            'Generic landing-page CRUD apps',
            'Copy-and-design content',
          ]} />
          <Risk title="Medium — defensible with workflow + data" tone="warning" items={[
            'Photography culling tools (Chronicle)',
            'Podcast production suites',
            'Generic CRMs',
            'Generic dashboards',
          ]} />
          <Risk title="Low risk — the moat is operations" tone="success" items={[
            'EdTech with FERPA/COPPA discipline (TSA, drijezie)',
            'Healthcare CTE platforms (CompassRN/AscendantCare)',
            'On-set production coordination (Callboard)',
            'Series LLC / asset orchestration tools',
            'Pastoral-warmth-required workflows',
          ]} />
        </div>

        <div className="bg-info/8 border border-info/30 rounded-lg p-4 mt-4">
          <CardTitle className="!text-base !text-info mb-2">Strategic implication</CardTitle>
          <p className="font-body text-sm text-cream/80 leading-relaxed">
            Stop competing on the commoditized layer. Use AI to speed your <em>infrastructure</em>
            work and reinvest the saved hours into the layers AI can't touch — the trust,
            the compliance posture, the on-set human judgment, the pastoral relationship
            with school-district decision-makers and worried parents. That is your durable
            moat. Anyone can build the dashboard. Almost no one can also be the person Justin
            calls to tell him how to film his late father's friends without anyone crying
            on camera.
          </p>
        </div>
      </Card>
    </section>
  )
}

/* helpers */

function SectionMarker({ icon, eyebrow, title }: { icon: React.ReactNode; eyebrow: string; title: string }) {
  return (
    <div className="flex items-end gap-4 mb-5 pb-3 border-b border-bronze/15">
      <div className="text-bronze">{icon}</div>
      <div>
        <div className="eyebrow">{eyebrow}</div>
        <h2 className="font-heading text-2xl text-cream tracking-tight">{title}</h2>
      </div>
    </div>
  )
}

function Pillar({ num, title, body }: { num: string; title: string; body: string }) {
  return (
    <div className="bg-navy/40 border border-bronze/15 rounded-lg p-4">
      <div className="font-display italic text-3xl text-gold-soft font-light leading-none mb-3">{num}</div>
      <div className="eyebrow !text-bronze mb-2">{title}</div>
      <p className="font-body text-sm text-cream/75 leading-relaxed">{body}</p>
    </div>
  )
}

function TaxFrame({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <div className="bg-navy/40 border border-cream/8 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <span className="font-display italic text-3xl text-gold-soft font-light leading-none shrink-0">{n}</span>
        <div>
          <div className="font-heading text-base text-cream mb-1">{title}</div>
          <p className="font-body text-sm text-cream/70 leading-relaxed">{body}</p>
        </div>
      </div>
    </div>
  )
}

function AllocCard({ pct, icon, title, body, tone }: {
  pct: number; icon: React.ReactNode; title: string; body: string
  tone: 'warning' | 'gold' | 'info' | 'success' | 'bronze' | 'cream'
}) {
  const tones: Record<string, string> = {
    warning: 'border-warning/30 text-warning',
    gold: 'border-gold/30 text-gold-soft',
    info: 'border-info/30 text-info',
    success: 'border-success/30 text-success',
    bronze: 'border-bronze/30 text-bronze',
    cream: 'border-cream/20 text-cream',
  }
  return (
    <div className={cn('rounded-lg border bg-navy/40 p-4 flex gap-4 items-start', tones[tone].split(' ')[0])}>
      <div className={cn('shrink-0 w-16 text-center')}>
        <div className={cn('font-display italic text-4xl font-light leading-none', tones[tone].split(' ')[1])}>
          {pct}
        </div>
        <div className="font-mono text-[9px] text-cream/45 mt-1">PERCENT</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className={cn('flex items-center gap-1.5 eyebrow mb-1.5', tones[tone].split(' ')[1])}>{icon}<span>{title}</span></div>
        <p className="font-body text-sm text-cream/75 leading-relaxed">{body}</p>
      </div>
    </div>
  )
}

function Stage({ n, body }: { n: string; body: string }) {
  return (
    <div className="border-l-2 border-bronze/40 pl-4 py-1">
      <div className="font-heading text-base text-cream mb-1">{n}</div>
      <p className="font-body text-sm text-cream/75 leading-relaxed">{body}</p>
    </div>
  )
}

function Risk({ title, tone, items }: { title: string; tone: 'danger' | 'warning' | 'success'; items: string[] }) {
  const t = {
    danger: { border: 'border-danger/40', text: 'text-danger', bg: 'bg-danger/5' },
    warning: { border: 'border-warning/40', text: 'text-warning', bg: 'bg-warning/5' },
    success: { border: 'border-success/40', text: 'text-success', bg: 'bg-success/5' },
  }[tone]
  return (
    <div className={cn('rounded-lg border p-4', t.border, t.bg)}>
      <div className={cn('eyebrow mb-3', t.text)}>{title}</div>
      <ul className="space-y-1.5">
        {items.map((i) => (
          <li key={i} className="font-body text-sm text-cream/75 flex gap-2">
            <span className={t.text}>·</span><span>{i}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// suppress unused warnings for icons
void ScrollText
